import PropTypes from 'prop-types';
import WinnerPhoto from './WinnerPhoto';
import { cardImageUrl } from '@/lib/images/cardImage';

function dateLabel(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', {
    timeZone: 'Europe/London',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

/**
 * One draw result, led by a big square picture (Rob: real winners with their
 * prizes are a big trust signal). The winner's photo with their prize when
 * they agreed to it being shown, marked "Winner"; otherwise the prize photo.
 * Then the date, prize, winning ticket and who won.
 */
export default function ResultRow({ r }) {
  const withPhoto = r.people?.find((p) => p.photo);
  const pictureClass = 'aspect-square w-full';

  return (
    <li className="grid grid-cols-[6.5rem_minmax(0,1fr)] sm:grid-cols-[10rem_minmax(0,1fr)] lg:grid-cols-[13rem_minmax(0,1fr)_auto_15rem] items-center gap-x-5 sm:gap-x-8 gap-y-3 py-5 sm:py-6 border-b border-ink/15">
      <div className="relative row-span-3 lg:row-span-1 self-start lg:self-center">
        {withPhoto ? (
          <>
            <WinnerPhoto
              src={withPhoto.photo}
              alt={`${withPhoto.name} with their prize: ${r.prize}`}
              sizes="(min-width: 1024px) 208px, (min-width: 640px) 160px, 104px"
              className={pictureClass}
            />
            <span className="absolute left-1.5 top-1.5 bg-accent px-1.5 py-0.5 text-[0.62rem] font-black uppercase tracking-[0.08em] text-accent-foreground">
              Winner
            </span>
          </>
        ) : r.prizeImage ? (
          <img
            src={cardImageUrl(r.prizeImage)}
            alt={`The prize: ${r.prize}`}
            loading="lazy"
            className={`${pictureClass} object-cover`}
          />
        ) : (
          <span className={`${pictureClass} flex items-center justify-center bg-black/[0.05]`} aria-hidden="true">
            <img src="/assets/images/rendalls-mark.png" alt="" className="w-1/2 opacity-30" />
          </span>
        )}
      </div>

      <div className="min-w-0">
        <p className="text-sm font-semibold text-ink-soft tabular-nums">{dateLabel(r.date)}</p>
        <p className="d mt-1 [font-variation-settings:'wdth'_108] font-extrabold normal-case text-[clamp(1.15rem,1.8vw,1.6rem)] leading-[1.05]">
          {r.prize}
        </p>
        {(r.replayUrl || r.entriesUrl) && (
          <p className="mt-2 flex flex-wrap gap-x-4 text-sm font-semibold">
            {r.replayUrl && (
              <a href={r.replayUrl} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">
                Watch the draw
              </a>
            )}
            {r.entriesUrl && (
              <a href={r.entriesUrl} className="underline underline-offset-2">
                Entry list
              </a>
            )}
          </p>
        )}
      </div>

      <div className="col-start-2 lg:col-start-auto flex items-center gap-3 lg:justify-end">
        {r.ticket && (
          <span className="stub" aria-label={`Winning ticket ${r.ticket}`}>
            <span className="text-[0.66rem] font-bold uppercase tracking-[0.08em]">No.</span>
            <span className="num text-[1.35rem]">{r.ticket}</span>
          </span>
        )}
      </div>

      <div className="col-start-2 lg:col-start-auto flex flex-col gap-1 font-semibold">
        {r.people?.length ? (
          r.people.map((p, i) => (
            <span key={i}>
              {p.name}
              {p.location ? <span className="text-ink-soft font-medium">, {p.location}</span> : null}
            </span>
          ))
        ) : (
          <span className="text-ink-soft font-medium">Winner being confirmed</span>
        )}
      </div>
    </li>
  );
}

ResultRow.propTypes = { r: PropTypes.object.isRequired };
